import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Banner,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  InlineStack,
  Panel,
  PanelSection,
  Paragraph,
  Spinner,
  TextField,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAnalytics } from '@/services/analytics';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import {
  isSegmentOutLegacyDirty,
  segmentOutLegacyEventNames,
  type SegmentOutLegacyIntegration,
} from '@/services/db/segment-out-legacy/entity';
import { getSegmentOutLegacyQueryOptions } from '@/services/db/segment-out-legacy/queries';
import { useRemoveSegmentOutLegacy, useSaveSegmentOutLegacy } from '@/services/db/segment-out-legacy/mutations';
import { disableStartedProperties } from '../analytics';
import { integrationsOverviewHref } from '../hooks';
import {
  notifyConnectorRemoveFailed,
  notifyConnectorRemoved,
  notifySettingsSaveFailed,
  notifySettingsSaved,
} from '../snackbars';

type LegacySegmentOutConnectorProps = {
  workspaceId: string;
  /**
   * The Segment region off the `segment_out` record, resent with every save.
   *
   * The legacy read does not carry it and the update rejects a body without it, so it
   * has to come from the record that chose this form. That record is a faithful
   * source, which matters because the save replaces the field: for a row with legacy
   * settings the `segment_out` read answers `EU: s.SegmentOut.EU` — the legacy block's
   * own value — rather than a default (`ui_api/integrations/dom.go`). Confirmed on
   * production: a legacy record created with `eu: true` reads back `eu: true` with
   * `use_javascript: false`, and is still `eu: true` after this form saves.
   */
  eu: boolean;
};

/**
 * The older generation of the Segment source connector.
 *
 * Two generations of this connector exist and only one is configurable through the
 * current form: a record whose settings predate it is written through the
 * `segdotcom` endpoints, which take a different body. So the page picks a form from
 * the record rather than showing one that would write to the wrong place.
 *
 * What the reader can do here is narrower, and that is Ember's shape rather than a
 * simplification: choose which events are sent, or remove the connector. There is no
 * enable toggle, send-frequency choice or region choice, because the legacy form never
 * offered one — not because the record cannot hold them. It can: the legacy settings
 * block carries `SendDuplicates` and `EU`, and the legacy *read* is what omits them.
 * The region therefore has to be read from the `segment_out` record and resent, which
 * is why this component takes an `eu` prop.
 *
 * The write key is editable through this endpoint too — it is rewritten on every
 * update, not only on create — but the form keeps it read-only, as Ember's did, and
 * sends back the value it read.
 */
export function LegacySegmentOutConnector({ workspaceId, eu }: LegacySegmentOutConnectorProps) {
  const record = useQuery(getSegmentOutLegacyQueryOptions(Number(workspaceId)));

  if (record.isPending) {
    return (
      <Panel withSectionBorders>
        <PanelSection>
          <Box textAlign="center" p="300">
            <Spinner />
          </Box>
        </PanelSection>
      </Panel>
    );
  }

  // A read that failed is not a record that is absent, and the difference decides what
  // the reader sees — a form with a blank key invites a save that overwrites the real
  // selection. Ember put a "Try again" button here that re-ran the fetch in place; this
  // asks for a reload instead, which is the page's own idiom but a coarser affordance.
  // `null` lands here too: the `segment_out` record said this workspace has a legacy
  // one, so a 404 means the two reads disagree — a fault, not an empty state to render.
  if (record.isError || record.data == null) {
    return (
      <Banner
        variant="critical"
        title="We couldn't load this integration"
        description="Refresh the page to try again."
      />
    );
  }

  return <LegacyForm workspaceId={workspaceId} eu={eu} saved={record.data} />;
}

type LegacyFormProps = {
  workspaceId: string;
  eu: boolean;
  saved: SegmentOutLegacyIntegration;
};

/**
 * Split from the component above so the event draft is seeded from a record that
 * has loaded, rather than from nothing and then synchronised to one.
 */
function LegacyForm({ workspaceId, eu, saved }: LegacyFormProps) {
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const safeNavigate = useSafeNavigate();

  const workspace = Number(workspaceId);
  const isReadOnly = !hasPermission('journeys/segment_integration/edit');
  // Removing is its own grant. Ember's legacy form gated this on `edit`, which let a
  // role given edit alone tear down a connection it was never granted — the same
  // correction the current form's connect half already carries.
  const canRemove = hasPermission('journeys/segment_integration/delete');

  // Only the reader's changes, laid over the saved record. Seeding the whole map
  // instead would pin the draft to the catalogue as it looked when the form mounted:
  // a name a later read added would render unchecked whatever the server said, and the
  // next save — which replaces the whole selection — would write it off.
  const [edits, setEdits] = useState<Record<string, boolean>>({});
  const selection = { ...saved.events, ...edits };

  const save = useSaveSegmentOutLegacy({
    workspaceId: workspace,
    mutationConfig: {
      onSuccess: () => notifySettingsSaved(snackbars),
      onError: () => notifySettingsSaveFailed(snackbars),
    },
  });

  const remove = useRemoveSegmentOutLegacy({
    workspaceId: workspace,
    mutationConfig: {
      onSuccess: () => notifyConnectorRemoved(snackbars),
      onError: () => notifyConnectorRemoveFailed(snackbars),
    },
  });

  // Ember's legacy save bar: dirty, plus the edit permission. There is no
  // at-least-one-event rule here — that belongs to the current form, and applying it
  // would strand a reader who wants to stop forwarding everything while keeping the
  // connector, which on this form is what unchecking every box says.
  const canSave = isSegmentOutLegacyDirty(selection, saved.events) && !isReadOnly;

  // Ember's save bar also had a Reset that restored the loaded selection in place. This
  // follows the current form instead, where Cancel leaves without saving — the same
  // outcome by a different route, and one interaction model on one page.
  return (
    <>
      <Panel>
        <PanelSection>
          <InlineStack alignBlock="end" gap="100">
            <Box flexGrow="1">
              {/* Masked for session replay, as Ember's legacy branch was: this is a
                  third-party credential, and the API decrypts it to answer the read. */}
              <TextField className="fs-mask" label="Segment write key" value={saved.key} isDisabled />
            </Box>
            <Button
              variant="secondary"
              isCritical
              isDisabled={!canRemove || save.isPending}
              isLoading={remove.isPending}
              onClick={() => {
                // Reported as `Deleted`, the same event and the same wire metadata the
                // current form sends for its own remove. Both generations are one
                // connector to anyone reading the funnel, so they must not diverge here.
                void track('integration', 'integrationDisableStarted', disableStartedProperties('Deleted'));
                remove.mutate();
              }}
            >
              Remove integration
            </Button>
          </InlineStack>

          <Box mt="200">
            <Paragraph color="subtle" mb="200">
              Send the following events:
            </Paragraph>
            {segmentOutLegacyEventNames(saved.events).map((name) => (
              <Checkbox
                key={name}
                name={`event_${name}`}
                label={name}
                isChecked={selection[name] === true}
                isDisabled={isReadOnly}
                onCheckedChange={(checked) => setEdits((current) => ({ ...current, [name]: checked }))}
              />
            ))}
          </Box>
        </PanelSection>
      </Panel>

      <InlineStack alignInline="end" alignBlock="center" gap="200" mt="200">
        <ButtonGroup groupVariant="spaced">
          <Button variant="tertiary" onClick={() => safeNavigate(integrationsOverviewHref(workspaceId))}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isDisabled={!canSave || remove.isPending}
            isLoading={save.isPending}
            onClick={() => save.mutate({ key: saved.key, events: selection, eu })}
          >
            Save integration
          </Button>
        </ButtonGroup>
      </InlineStack>
    </>
  );
}
